/**
 * F-70 translate UI state: which column's dialog is open, and which starts are
 * in flight. Local view state only.
 *
 * What is deliberately NOT here: the run's PROGRESS. Since 2026-07-16 the job
 * is server-side and publishes `{status, done, total, failed}` onto the board
 * doc (`txJob`), so progress is read from the doc — that is what makes every
 * client show the same bar, and a reload mid-job still show it. A local mirror
 * of it would be a second source of truth that is wrong for everyone but the
 * user who clicked.
 *
 * `starting` is the one honest exception: the window between clicking Translate
 * and the job's first doc tick, which belongs to this browser alone.
 *
 * Module SINGLETON (unlike the comments/import stores, which BoardScreen owns
 * and passes down) because the F-27 registry is: `registerToolbarAction` runs
 * at module scope and its `run(ctx)` receives only a `BoardSession` — there is
 * no prop path from BoardScreen to the action. Every key is board-scoped, and
 * `TranslateLayer` clears the dialog on unmount, so nothing leaks across views.
 */
import { createStore, type StoreApi } from "zustand/vanilla";

/** The column a RUN is for. Board-scoped: the layer ignores others. */
export interface TranslateTarget {
  boardId: string;
  sheetId: string;
  colId: string;
}

/**
 * What the dialog opens for. `colId` null = "pick the target in the dialog":
 * the top-right pill's translate icon and the source column's toolbar icon
 * both open it this way (2026-09-03), offering the sheet's existing columns
 * plus a NEW column named after the language. The toolbar icon on a
 * non-source column still opens it with that column chosen.
 */
export interface TranslateDialogTarget {
  boardId: string;
  sheetId: string;
  colId: string | null;
}

/**
 * A "translate this cell" request in flight (cell-translate.ts) — this
 * browser's, for the spinner on the pill and the disabled row in the menu.
 * Board-scoped like everything else here. There is no doc state for it: the
 * answer is written under the user's own origin when it lands, and until then
 * nothing on the board has changed for anyone.
 */
export interface CellTranslateRun {
  boardId: string;
  sheetId: string;
  /** How many cells the request carries — the row's "Translating 3 cells…". */
  count: number;
}

export interface TranslateUiState {
  /** The open dialog's target, or null. */
  dialog: TranslateDialogTarget | null;
  /** The cell request in flight, or null. */
  cellRun: CellTranslateRun | null;
  /**
   * Run keys whose POST has not answered yet (an array, so zustand sees a new
   * reference). Cleared as soon as the server answers — from then on the DOC's
   * job state is the truth, including for the client that started the run.
   */
  starting: readonly string[];
  openDialog(target: TranslateDialogTarget): void;
  closeDialog(): void;
  markStarting(key: string): void;
  clearStarting(key: string): void;
  setCellRun(run: CellTranslateRun | null): void;
}

/** Identity of a run: exactly the server's C5.3 in-flight key (board+sheet+col). */
export function runKey(target: TranslateTarget): string {
  return `${target.boardId}:${target.sheetId}:${target.colId}`;
}

export type TranslateUiStore = StoreApi<TranslateUiState>;

export const translateUi: TranslateUiStore = createStore<TranslateUiState>((set) => ({
  dialog: null,
  cellRun: null,
  starting: [],
  openDialog: (dialog) => set({ dialog }),
  setCellRun: (cellRun) => set({ cellRun }),
  closeDialog: () => set({ dialog: null }),
  markStarting: (key) =>
    set((s) => (s.starting.includes(key) ? s : { starting: [...s.starting, key] })),
  clearStarting: (key) => set((s) => ({ starting: s.starting.filter((k) => k !== key) })),
}));

/** Is this column's start still in flight? (Re-entry guard — F-70 + C5.3.) */
export function isStarting(starting: readonly string[], key: string): boolean {
  return starting.includes(key);
}

/**
 * Is ANY column on this sheet being started? The picker dialog has no column
 * yet, and the server allows one run per board anyway (C5.3), so this is the
 * question it asks.
 */
export function isStartingOnSheet(
  starting: readonly string[],
  boardId: string,
  sheetId: string,
): boolean {
  const prefix = `${boardId}:${sheetId}:`;
  return starting.some((key) => key.startsWith(prefix));
}
